import { useEffect, useId, useMemo, useState } from "react";
import type { FormEvent, MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { demoApi, problemMessage } from "@/api/client";
import { useReferenceData } from "@/hooks/useReferenceData";
import type { Guid, ReconciliationReport } from "@/api/types";
import "./ReportsReconciliation.css";

type ReportStatus = "idle" | "loading" | "ready" | "error";

/** Today, UTC — same server-clock default `ReconciliationReportEndpoints`' own `asOf`
 *  defaulting uses, restated here only for the form's own initial value
 *  (`ReportsTrialBalance.tsx`'s own `defaultAsOf`, copied). */
function defaultAsOf(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Cents-precise, no currency symbol — matches `ReconciliationCsv`'s own two-decimal,
 *  no-`$` number face, so the on-screen figure and the downloaded file always read the
 *  same amount the same way (`ReportsTrialBalance.tsx`'s own `formatAmount`, copied). */
function formatAmount(amount: number): string {
  return amount.toFixed(2);
}

/**
 * Bank reconciliation detail — Reports' record of every reconciled entry on one (asset)
 * account through a caller-chosen date, the cleared balance those lines cumulate to, and
 * the bank statement lines still open as of that same date. ef-055-1, ADR 0009 §6
 * ("Reports is one picker" — a report renders inside the template at `/reports/{slug}`,
 * no area tab strip of its own; "All reports" below is the only way back).
 *
 * <p>
 * ACCOUNT PICKER, NOT A SERVER DEFAULT — `accountId` is required and restricted to
 * asset-kind accounts, same shape `BooksReconcile.tsx`'s own picker already uses,
 * defaulting to the first asset account once the reference data loads.
 * </p>
 *
 * <p>
 * NOTHING IS FETCHED UNTIL Show IS PRESSED — same "no guessed window" discipline
 * `ReportsTrialBalance.tsx`'s own remarks give, restated here for the identical reason: a
 * reconciliation read against an account/date nobody asked for is a number a farmer did
 * not request. The As of input still carries today's date as a sensible default so the
 * form is never an empty field.
 * </p>
 */
export function ReportsReconciliation() {
  const { accounts, status: refStatus, error: refError } = useReferenceData();
  const assetAccounts = useMemo(() => accounts.filter((a) => a.kind === "Asset"), [accounts]);

  const accountFieldId = useId();
  const asOfId = useId();

  const [accountId, setAccountId] = useState<Guid | "">("");
  useEffect(() => {
    if (accountId === "" && assetAccounts.length > 0) {
      setAccountId(assetAccounts[0]!.id);
    }
  }, [assetAccounts, accountId]);

  const [asOf, setAsOf] = useState(defaultAsOf);

  const [report, setReport] = useState<ReconciliationReport | null>(null);
  const [status, setStatus] = useState<ReportStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const canSubmit = accountId !== "" && asOf !== "" && status !== "loading";

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit || accountId === "") return;

    setStatus("loading");
    setError(null);
    try {
      const result = await demoApi.getReconciliationReport({ accountId, asOf });
      setReport(result);
      setStatus("ready");
    } catch (err) {
      setReport(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load the reconciliation report."));
    }
  }

  /**
   * The export is its own request, fired on the click — not fetched alongside the
   * report, same reasoning `ReportsTrialBalance.tsx`'s identical download gives: paying
   * for the whole CSV body on every date change would be wasted work for the visits that
   * never press the button.
   */
  const [csvState, setCsvState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "done"; rows: number } | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function downloadCsv(): Promise<void> {
    if (!canSubmit || accountId === "") return;
    setCsvState({ kind: "busy" });
    try {
      const csv = await demoApi.getReconciliationCsv({ accountId, asOf });

      // The bytes are the server writer's output verbatim — same "add a blob and a
      // filename and nothing else" discipline `ReportsTrialBalance.tsx`'s identical
      // download follows.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `easyfarm-reconciliation-${asOf}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const rows = Math.max(csv.trim().split("\n").length - 1, 0);
      setCsvState({ kind: "done", rows });
    } catch (err) {
      setCsvState({
        kind: "error",
        message: problemMessage(err, "Could not build the reconciliation file."),
      });
    }
  }

  function handleAllReportsClick(event: MouseEvent<HTMLAnchorElement>): void {
    event.preventDefault();
    // Self-contained, same device every other report screen's own handler already uses
    // rather than threading a `navigate` prop through every screen this job touches:
    // pushState doesn't fire popstate on its own, so App.tsx's existing listener is told
    // directly.
    window.history.pushState({}, "", "/reports");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  const reconciledLines = report?.reconciledLines ?? [];
  const openStatementLines = report?.openStatementLines ?? [];
  const isEmpty = status === "ready" && reconciledLines.length === 0 && openStatementLines.length === 0;

  return (
    <main className="reconciliation">
      <PageHeader
        title="Bank reconciliation detail"
        subtitle="Cleared entries and open bank statement lines for one account, as of a date you choose."
      />

      <a href="/reports" className="reconciliation__all-reports" onClick={handleAllReportsClick}>
        ← All reports
      </a>

      <section
        className="reconciliation__panel"
        aria-labelledby="reconciliation-heading"
        data-state={isEmpty ? "empty" : status}
      >
        <h2 id="reconciliation-heading" className="reconciliation__heading">
          Reconciliation
        </h2>

        <form className="reconciliation__form" onSubmit={(event) => void handleSubmit(event)}>
          <div className="reconciliation__field">
            <label htmlFor={accountFieldId}>Account</label>
            <select
              id={accountFieldId}
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              disabled={refStatus === "loading" || assetAccounts.length === 0}
            >
              {assetAccounts.length === 0 && <option value="">No instrument accounts yet</option>}
              {assetAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.number} — {a.name}
                </option>
              ))}
            </select>
          </div>

          <div className="reconciliation__field">
            <label htmlFor={asOfId}>As of</label>
            <input
              id={asOfId}
              type="date"
              value={asOf}
              onChange={(event) => setAsOf(event.target.value)}
            />
          </div>

          <button type="submit" className="btn btn--primary reconciliation__show" disabled={!canSubmit}>
            {status === "loading" ? "Loading…" : "Show"}
          </button>
        </form>

        {refStatus === "error" && (
          <StateMessage tone="error" title="Could not load accounts." detail={refError ?? undefined} />
        )}

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the reconciliation report."
            detail={error ?? undefined}
          />
        )}

        {isEmpty && (
          <StateMessage
            tone="empty"
            title="Nothing to reconcile as of this date."
            detail="No reconciled entries and no open statement lines on this account."
          />
        )}

        {status === "ready" && report && !isEmpty && (
          <>
            <p className="reconciliation__cleared">
              Cleared balance as of {report.asOf}: <strong>{formatAmount(report.clearedBalance)}</strong>
            </p>

            <div
              className="reconciliation__table-wrap"
              tabIndex={0}
              role="group"
              aria-labelledby="reconciliation-heading"
            >
              <table className="reconciliation__table ruled-rows">
                <caption className="reconciliation__caption">
                  {reconciledLines.length} reconciled {reconciledLines.length === 1 ? "line" : "lines"}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Occurred</th>
                    <th scope="col">Reconciled</th>
                    <th scope="col">Side</th>
                    <th scope="col" className="reconciliation__num">Amount</th>
                    <th scope="col" className="reconciliation__num">Running balance</th>
                    <th scope="col">Memo / Vendor</th>
                  </tr>
                </thead>
                <tbody>
                  {reconciledLines.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="reconciliation__no-lines">
                        No reconciled lines as of this date.
                      </td>
                    </tr>
                  ) : (
                    reconciledLines.map((line) => (
                      <tr key={line.entryLineId}>
                        <td>{line.occurredOn}</td>
                        <td>{line.reconciledOn}</td>
                        <td>{line.side}</td>
                        <td className="reconciliation__num reconciliation__amount">
                          {formatAmount(line.amount)}
                        </td>
                        <td className="reconciliation__num reconciliation__amount">
                          {formatAmount(line.runningBalance)}
                        </td>
                        <td>{line.vendorName ?? line.memo ?? ""}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="reconciliation__open" aria-labelledby="reconciliation-open-heading">
              <h3 id="reconciliation-open-heading" className="reconciliation__open-heading">
                Open statement lines ({openStatementLines.length})
              </h3>
              {openStatementLines.length === 0 ? (
                <p className="reconciliation__open-empty">Nothing outstanding as of this date.</p>
              ) : (
                <ul className="reconciliation__open-list">
                  {openStatementLines.map((line) => (
                    <li key={line.id} className="reconciliation__open-item">
                      <span className="reconciliation__open-date">{line.occurredOn}</span>
                      <span className="reconciliation__open-amount">{formatAmount(line.amount)}</span>
                      <span className="reconciliation__open-description">{line.description}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="reconciliation__export">
              <h3 className="reconciliation__export-title">Reconciliation for your records</h3>
              <p className="reconciliation__export-copy">
                The reconciled lines above, as a CSV a spreadsheet can open.
              </p>
              <button
                type="button"
                className="btn btn--secondary reconciliation__export-btn"
                disabled={csvState.kind === "busy" || !canSubmit}
                onClick={() => void downloadCsv()}
              >
                {csvState.kind === "busy" ? "Building the file…" : "Download CSV"}
              </button>

              {csvState.kind === "done" && (
                <p className="reconciliation__export-result" role="status">
                  {csvState.rows} {csvState.rows === 1 ? "line" : "lines"} saved.
                </p>
              )}

              {csvState.kind === "error" && (
                <p className="reconciliation__export-error" role="alert">
                  {csvState.message} Nothing was downloaded.
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
